import {
  assistantCitationMessageIdentity,
  matchesAssistantCitationMessage,
} from "./assistantCitationMessageIdentity";
import { use, useEffect, useRef, type ReactNode } from "react";
import { TimelineCitationContext } from "./timelineCitationContext";
import { observeAssistantCitationSource } from "./observeAssistantCitationSource";
import type { MessageId } from "@cmmd-forge/contracts";
import { parseScopedThreadKey } from "@cmmd-forge/client-runtime";

export function AssistantCitationContent({
  routeThreadKey,
  messageId,
  itemKey,
  sourceEventId,
  children,
}: {
  readonly routeThreadKey: string;
  readonly messageId: MessageId;
  readonly itemKey?: string;
  readonly sourceEventId?: string | undefined;
  readonly children: ReactNode;
}) {
  const context = use(TimelineCitationContext);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const target = context?.target;
  const listRef = context?.listRef;
  useEffect(() => {
    const root = rootRef.current;
    const list = listRef?.current;
    if (
      !root ||
      !list ||
      !target ||
      !matchesAssistantCitationMessage(
        { id: messageId, ...(sourceEventId ? { sourceEventId } : {}) },
        target.citation.messageId,
      )
    )
      return;
    return observeAssistantCitationSource({
      root,
      itemKey: itemKey ?? messageId,
      request: target,
      list,
    });
  }, [target, listRef, itemKey, messageId, sourceEventId]);
  const threadRef = parseScopedThreadKey(routeThreadKey);
  return (
    <div
      ref={rootRef}
      data-assistant-citation-source={
        threadRef
          ? assistantCitationMessageIdentity({
              id: messageId,
              ...(sourceEventId ? { sourceEventId } : {}),
            })
          : undefined
      }
      data-assistant-citation-environment={threadRef?.environmentId}
      data-assistant-citation-thread={threadRef?.threadId}
    >
      {children}
    </div>
  );
}
